import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { ObligationAssignment } from '../types/api';

/**
 * The three Land Data host entities an obligation may be attached to (spec
 * docs/superpowers/specs/2026-08-16-land-data-obligations-design.md §3.2/
 * §4.2) — same `ObligationTargetType` shape as
 * api/src/lib/obligation-assignment-router.ts.
 */
export type ObligationAssignmentHost = 'lease' | 'contract' | 'well';

const HOST_BASE_PATH: Record<ObligationAssignmentHost, string> = {
  lease: '/leases',
  contract: '/contracts-land',
  well: '/wells',
};

export const obligationAssignmentsKeys = {
  all: ['obligation-assignments'] as const,
  detail: (host: ObligationAssignmentHost, projectId: string | null, id: string) =>
    [...obligationAssignmentsKeys.all, host, projectId, id] as const,
};

export function useObligationAssignments(host: ObligationAssignmentHost, id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id
      ? obligationAssignmentsKeys.detail(host, projectId, id)
      : [...obligationAssignmentsKeys.all, host, 'none'],
    enabled: id != null && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ obligations: ObligationAssignment[] }>(
        `${HOST_BASE_PATH[host]}/${id}/obligations`,
      );
      return res.obligations;
    },
  });
}

export interface AddObligationAssignmentInput {
  obligation_id: string;
  date_value?: string | null | undefined;
  days_value?: number | null | undefined;
}

interface AddObligationAssignmentResponse {
  assignment: ObligationAssignment;
}

/** Attaches an obligation to `id` (of `host`), carrying its per-attachment
 * value. Invalidates this host+id's assignment list on success. */
export function useAddObligationAssignment(host: ObligationAssignmentHost, id: string | null) {
  const qc = useQueryClient();
  const projectId = useProjectStore((s) => s.projectId);
  return useMutation({
    mutationFn: (input: AddObligationAssignmentInput) => {
      if (id == null) {
        return Promise.reject(
          new Error('Cannot add an obligation before the record is saved.'),
        );
      }
      return api.post<AddObligationAssignmentResponse>(
        `${HOST_BASE_PATH[host]}/${id}/obligations`,
        input,
      );
    },
    onSuccess: () => {
      if (id == null) return;
      qc.invalidateQueries({ queryKey: obligationAssignmentsKeys.detail(host, projectId, id) });
    },
  });
}

/** Detaches (soft-deletes) an obligation_assignments row by its own id. */
export function useRemoveObligationAssignment(host: ObligationAssignmentHost, id: string | null) {
  const qc = useQueryClient();
  const projectId = useProjectStore((s) => s.projectId);
  return useMutation({
    mutationFn: (assignmentId: string) => {
      if (id == null) {
        return Promise.reject(
          new Error('Cannot remove an obligation before the record is saved.'),
        );
      }
      return api.delete(`${HOST_BASE_PATH[host]}/${id}/obligations/${assignmentId}`);
    },
    onSuccess: () => {
      if (id == null) return;
      qc.invalidateQueries({ queryKey: obligationAssignmentsKeys.detail(host, projectId, id) });
    },
  });
}
